import { Flex } from "monday-ui-react-core";
import type { ChangeEvent } from "react";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { CloneItemsProvider } from "@/components/CloneView/CloneItemProvider";
import { CloneItems } from "@/components/CloneView/components/CloneItems/CloneItems";
import { CloneItemsButton } from "@/components/CloneView/components/CloneItemsButton/CloneItemsButton";
import type { CloneOptions } from "@/components/CloneView/components/CloneOptions/CloneOptionsSection";
import {
  CloneOptionsSection,
  DEFAULT_OPTIONS_STATE,
  DEFAULT_POSTFIX,
} from "@/components/CloneView/components/CloneOptions/CloneOptionsSection";
import { EmptySection } from "@/components/CloneView/components/EmptySection/EmptySection";
import { SelectTargetBoard } from "@/components/CloneView/components/SelectTargetBoard/SelectTargetBoard";
import { getSearchMetrics } from "@/components/CloneView/state/cloneContextReducer";
import LoadingPage from "@/components/LoadingPage";
import { clonePlusDialog } from "@/lib/monday";
import type { MondayService } from "@/services/monday-service";
import { VALIDATION_STATUS, valueCreatedForMonday } from "@/services/monday-service";

import { validateText } from "./utils";

interface Props {
  mondayService: MondayService;
}

export default function CloneView({ mondayService }: Props) {
  const [boardId, setBoardId] = useState<number>(-1);
  const [selectedTargetBoard, setSelectedTargetBoard] = useState<string | null>(null);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [cloneItems, setCloneItems] = useState<string[]>([]);
  const [slug, setSlug] = useState<string>("");
  const [clearTargetBoardValue, setClearTargetBoardValue] = useState<boolean>(false);

  const width = "58vw";
  const height = "68vh";

  const [postfix, setPostfix] = useState<string>(DEFAULT_POSTFIX);
  const [cloneOptions, setCloneOptions] = useState<CloneOptions>(DEFAULT_OPTIONS_STATE);
  const { data: metrics, isLoading: isLoadingMetrics } = useSelector(getSearchMetrics);

  useEffect(() => {
    void (async () => {
      const slug = await mondayService.getAccountSlug();
      setSlug(slug);
    })()
      .then(() => {})
      .catch(() => {});
  }, [mondayService]);

  const onBoardChange = (boardId: string) => {
    setSelectedTargetBoard(boardId);
  };

  const onPostfixChange = (text: string) => {
    setPostfix(text);
  };
  const resetFields = () => {
    setCloneItems([]);
    setSelectedItems([]);
    setPostfix(DEFAULT_POSTFIX);
    setCloneOptions(DEFAULT_OPTIONS_STATE);
    setClearTargetBoardValue(!clearTargetBoardValue);
  };

  const configHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const { id } = e.target;
    const v = !cloneOptions[id];
    const value = { ...cloneOptions, [id]: v } as CloneOptions;
    setCloneOptions(value);
  };

  const onCloneClose = (data: { dataJson?: string } = {}) => {
    resetFields();
    if (data.dataJson) {
      const { redirectToTargetBoard, retryAgain } = JSON.parse(data.dataJson) as {
        redirectToTargetBoard: boolean;
        retryAgain: boolean;
      };
      if (redirectToTargetBoard) {
        const host = `https://${slug}.monday.com`;
        // Redirect to the target board
        clonePlusDialog.openLinkInTab(`${host}/boards/${selectedTargetBoard}`);
      } else if (retryAgain) {
        // TODO: do retry
      }
    }
  };

  const onCloneItemButtonClick = () => {
    clonePlusDialog
      .showModalDialog(
        { dataJson: JSON.stringify({ selectedItems, selectedTargetBoard, cloneOptions, postfix }) },
        "/cloneProgress",
        width,
        height,
        onCloneClose
      )
      .then(() => {})
      .catch(() => {})
      .finally(() => {
        valueCreatedForMonday();
      });
  };
  const shouldBeDisabled = () => {
    const isPostFixValid = validateText(postfix);
    if (selectedItems.length === 0 || selectedTargetBoard === undefined || isPostFixValid === VALIDATION_STATUS.ERROR) {
      return true;
    }
    return false;
  };

  if (isLoadingMetrics) {
    return <LoadingPage label="Loading..." />;
  }

  if (!metrics.length && !isLoadingMetrics) {
    return <EmptySection />;
  }

  return (
    <Flex direction={Flex.directions.COLUMN} align={Flex.align.START} gap={Flex.gaps.MEDIUM}>
      <CloneItemsProvider selectedItems={cloneItems}>
        {({ items }) => (
          <CloneItems
            boardId={boardId}
            setBoardId={setBoardId}
            items={items}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            setCloneItems={setCloneItems}
          ></CloneItems>
        )}
      </CloneItemsProvider>
      <SelectTargetBoard onBoardChange={onBoardChange} clearValue={clearTargetBoardValue} />
      <CloneOptionsSection
        cloneOptions={cloneOptions}
        onOptionChange={configHandler}
        postfix={postfix}
        onPostfixChange={onPostfixChange}
      />
      <CloneItemsButton onCloneItemButtonClick={() => void onCloneItemButtonClick()} isDisabled={shouldBeDisabled()} />
    </Flex>
  );
}
